import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm, useFormState } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import { Alert, Button, Divider, PasswordInput, Stack, Text, TextInput } from "@mantine/core";

import { CenteredCard } from "@/components/CenteredCard";
import { signUp } from "@/lib/authClient";
import { useAuthMethods } from "@/features/auth/hooks";
import { SocialButtons } from "@/features/auth/components/SocialButtons";
import { authSearchSchema, safeCallbackUrl } from "@/features/auth/redirect";
import { AuthMethodsStatus } from "@/features/auth/components/AuthMethodsStatus";

// minLength 8 matches the API's `emailAndPassword.minPasswordLength`. Keeping
// them in step means the user is told about a short password here rather than
// by a rejected request.
const signUpSchema = z.object({
  name: z.string().trim().min(1, "Enter your name."),
  email: z.email("Enter a valid email address."),
  password: z.string().min(8, "Use at least 8 characters."),
});

type SignUpValues = z.infer<typeof signUpSchema>;

function SignUpPage() {
  const { next } = Route.useSearch();
  const navigate = useNavigate();
  const { emailPasswordEnabled, registrationEnabled, socialProviders, isLoading, error: metadataError, retry, methods } = useAuthMethods();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
  } = useForm<SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { name: "", email: "", password: "" },
  });

  const { errors, isSubmitting } = useFormState({ control });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    if (!registrationEnabled || !emailPasswordEnabled) return;

    const { error } = await signUp.email({
      name: values.name,
      email: values.email,
      password: values.password,
    });

    if (error) {
      setFormError(error.message ?? "Could not create your account. Please try again.");
      return;
    }

    // No session yet: the API sets `autoSignIn: false` and requires a verified
    // email, and it has already sent a 6-digit code as part of sign-up
    // (`sendVerificationOnSignUp`). Verification is where the session is issued.
    void navigate({
      to: "/auth/verify-email",
      search: { email: values.email, next },
    });
  });

  return (
    <CenteredCard
      title="Create your account"
      description="You'll need an account to join a trip."
      footer={
        <Text size="sm" c="dimmed">
          Already have an account?{" "}
          <Text
            component="a"
            href="#"
            size="sm"
            c="var(--mantine-color-primary-text)"
            onClick={(event) => {
              event.preventDefault();
              void navigate({ to: "/auth/sign-in", search: { next } });
            }}
          >
            Sign in
          </Text>
        </Text>
      }
    >
      <Stack gap="md">
        <AuthMethodsStatus isLoading={isLoading} error={metadataError} retry={retry} noMethods={methods.length === 0} />
        {!isLoading && !metadataError && !registrationEnabled ? <Alert title="Registration unavailable">New-account registration is currently closed. Existing accounts can still sign in using enabled methods.</Alert> : null}
        {registrationEnabled ? <SocialButtons callbackURL={safeCallbackUrl(next)} /> : null}

        {registrationEnabled && emailPasswordEnabled ? (
          <>
            {socialProviders.length > 0 ? <Divider label="or" labelPosition="center" /> : null}

            <form onSubmit={(event) => void onSubmit(event)} noValidate>
              <Stack gap="md">
                <TextInput
                  label="Name"
                  autoComplete="name"
                  error={errors.name?.message}
                  {...register("name")}
                />
                <TextInput
                  label="Email"
                  type="email"
                  autoComplete="email"
                  error={errors.email?.message}
                  {...register("email")}
                />
                <PasswordInput
                  label="Password"
                  autoComplete="new-password"
                  description="At least 8 characters."
                  error={errors.password?.message}
                  {...register("password")}
                />

                {formError ? (
                  <Alert color="red" variant="light">
                    {formError}
                  </Alert>
                ) : null}

                <Button type="submit" size="md" fullWidth loading={isSubmitting}>
                  Create account
                </Button>
              </Stack>
            </form>
          </>
        ) : null}
      </Stack>
    </CenteredCard>
  );
}

export const Route = createFileRoute("/auth/sign-up")({
  validateSearch: authSearchSchema,
  component: SignUpPage,
});
